import { getAuth } from 'firebase/auth';
import { doc, getDoc, runTransaction, setDoc, getFirestore, serverTimestamp } from 'firebase/firestore';
import { addUserScopedWriteGuard } from './writes';

// Removed Membership and Account collections: no longer part of data model

export type UserContext = {
  isNewUser: boolean;
  userRef: ReturnType<typeof doc>;
};

async function createUserIfMissingTx(uid: string, _profileMetadata?: { providerId?: string; isAnonymous?: boolean }): Promise<{ isNewUser: boolean; userRef: ReturnType<typeof doc> }> {
  const db = getFirestore();
  const ref = doc(db, 'users', uid);
  console.log('🔍 createUserIfMissingTx called for UID:', uid);
  let created = false;
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) {
      // Do not create any fields on users/{uid}; leave document uncreated
      console.log('ℹ️ Skipping creation of users document fields for UID:', uid);
      created = false;
      return;
    }
    console.log('✅ Users document already exists for UID (no updates written):', uid);
  });
  return { isNewUser: created, userRef: ref };
}

// memberships removed

async function initializeUserDefaultsOnce(_uid: string): Promise<{ created: boolean }> {
  // No-op: do not create 'meta' subcollection
  return { created: false };
}

// active account selection removed

// loadAccountById removed

export async function loadUserContext(uid: string): Promise<UserContext> {
  // Enforce that context is for the current user only
  addUserScopedWriteGuard(uid, 'loadUserContext');

  const ensured = await createUserIfMissingTx(uid, undefined);
  // Do not create profile or defaults automatically; reads only
  return { isNewUser: ensured.isNewUser, userRef: ensured.userRef };
}



function generateNineDigitId(): string {
  const first = Math.floor(Math.random() * 9) + 1; // 1-9
  const rest = Array.from({ length: 8 }, () => Math.floor(Math.random() * 10)).join('');
  return `${first}${rest}`;
}

async function ensureUserProfileDocument(uid: string): Promise<void> {
  // No-op: do not create or modify profile docs automatically
  const db = getFirestore();
  const profileRef = doc(db, 'users', uid, 'user_profile', 'user_profile');

  const existing = await getDoc(profileRef);
  if (existing.exists()) {
    // Backfill immutables if missing/invalid on legacy docs; no global registry
    const root: any = existing.data() as any;
    const profilesArr: any[] = Array.isArray(root?.profiles) ? root.profiles : [];
    const first: any = profilesArr[0] || {};
    const accIdExisting = typeof first.account_id === 'string' ? first.account_id : '';
    const memIdExisting = typeof first.member_id === 'string' ? first.member_id : '';
    const is9 = (v: string) => /^[1-9]\d{8}$/.test(v);
    const needsAcc = !is9(accIdExisting);
    const needsMem = !is9(memIdExisting);
    if (!needsAcc && !needsMem) {
      return; // already valid
    }

    // Update missing identifiers without consulting a global registry; generate locally
    const accId = needsAcc ? generateNineDigitId() : accIdExisting;
    const memId = needsMem ? generateNineDigitId() : memIdExisting;
    const latest = await getDoc(profileRef);
    const curRoot: any = latest.exists() ? (latest.data() as any) : {};
    const curArr: any[] = Array.isArray(curRoot.profiles) ? curRoot.profiles : [];
    const curFirst: any = curArr[0] || {};
    const nextFirst = { ...curFirst };
    if (needsAcc) nextFirst.account_id = accId;
    if (needsMem) nextFirst.member_id = memId;
    const nextArr = curArr.length > 0 ? [{ ...curFirst, ...nextFirst }, ...curArr.slice(1)] : [nextFirst];
    await setDoc(profileRef, { profiles: nextArr, profile_lastupdated: serverTimestamp() } as any, { merge: false } as any);
    return;
  }

  // Do not create a new profile document for first-time users
  return;
}

